import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, Fab, IconButton, List, ListItem, TextField, Typography, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { CancelOutlined, Close, ExpandLess } from '@material-ui/icons';
import { motion } from "framer-motion";
import isEmpty from 'lodash.isempty';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ServiceLineItem } from './ServiceLineItem';
import { VirtuosoMUIComponents } from 'pages/CustomerBookingCreate/VirtuosoComponents';
import { useTrackEvent } from 'util/trackEvent';
import clsx from 'clsx';
import { reverse } from 'voca';

const Context = React.createContext({
  animationEnabled: true
});

const AnimatedContainer = props => {
  const { animationEnabled } = useContext(Context);
  return (
    <motion.div {...props} animate={animationEnabled ? true : undefined} />
  )
}
const useStyles = makeStyles(theme => ({
  topBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fixed: {
    flex: '0 0 auto'
  },
  fullHeight: {
    // minHeight: '60vh'
    height: `calc(100% - 200px)`
  },
  sharedHeight: {
    minHeight: '25vh'
  },
  swapPadding: {
    padding: 'unset',
    margin: theme.spacing(1, 3)
  }
}));
const ServiceAutocompleteList = ({
  service,
  title,
  subHeader,
  open,
  onClose = () => { },
  onSelect = async (items) => { },
  selectedItems = [],
  displayCoverage,
  children,
  forwardMapping,
  reverseMapping,
  comparator,
  compact = true,
}) => {
  const classes = useStyles()
  const [search, setSearch] = useState("");
  const trackEvent = useTrackEvent()

  const virtuoso = useRef(null)
  const initialOffsetIndex = useRef(0)
  const [isScrolling, setIsScrolling] = useState(false);
  const [items, setItems] = useState([]);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"),
    {
      noSsr: true,
    });

  const reverseMapIfAble = useCallback(
    (items) => {
      if (reverseMapping) {
        return items?.map((item) => reverseMapping(item))
      }
      return items
    },
    [reverseMapping],
  );

  const onScrollingStateChange = useCallback(
    (value) => {
      setIsScrolling(value);
    },
    [],
  );

  const filterItems = useCallback(
    () => {
      const items = reverseMapIfAble(service?.extended_package_items)
      if (isEmpty(search)) {
        setItems(items)
      } else {
        const similarFiltered = items?.filter((item) => {
          return (
            item?.label?.toLowerCase().includes(search?.toLowerCase()) ||
            item?.alternative_labels?.some((label) => label?.toLowerCase().includes(search?.toLowerCase()))
          )
        })
        setItems(similarFiltered)
      }
    },
    [service?.extended_package_items, search],
  );

  useEffect(
    () => {
      filterItems();
    }, [search]
  );

  useEffect(
    () => {
      setItems(reverseMapIfAble(service?.extended_package_items))
    }, [service?.extended_package_items]
  );
  const computeItemKey = useCallback(
    (index) => {
      return items?.[index]?.id;
    },
    [items],
  );

  const doClearItem = useCallback(
    async (item) => {
      const a = selectedItems.filter((i) => {
        if (comparator) {
          return !comparator(i, forwardMapping ? forwardMapping(item) : item)
        }
        return i.id !== item.id
      });
      await onSelect(a);
    },
    [selectedItems, comparator],
  );

  const doAddItem = useCallback(
    async (item) => {
      let mappedItem = {
        ...item,
        markedAsAvailable: true
      };
      if (forwardMapping) {
        mappedItem = forwardMapping(item);
      }
      const a = [...selectedItems, mappedItem];
      await onSelect(a);
    },
    [selectedItems],
  );

  const theme = useTheme()
  return (
    <>
      <DialogContent className={clsx(classes.swapPadding, classes.fixed)}>
        {subHeader}
      </DialogContent>
      <DialogContent className={clsx(classes.swapPadding, classes.fixed)}>
        <TextField
          fullWidth
          value={search}
          onChange={(e) => {
            trackEvent('Create Booking Service Autocomplete List - search')
            setSearch(e.target.value)
          }}
          placeholder="Search"
          autoFocus
          variant='outlined'
          InputProps={{
            endAdornment:
              !isEmpty(search) && (
                <IconButton
                  size="small"
                  onClick={() => setSearch("")}
                >
                  <CancelOutlined />
                </IconButton>
              )
          }}
        />

      </DialogContent>
      <DialogContent className={clsx(classes.swapPadding, !compact ? classes.fullHeight : classes.sharedHeight)} style={{ display: 'flex' }}>
        <Context.Provider value={{ animationEnabled: !isScrolling }}>
          <div style={{ flex: 1 }}>
            <Virtuoso
              components={VirtuosoMUIComponents}
              ref={virtuoso}
              totalCount={items?.length}
              computeItemKey={computeItemKey}
              scrollingStateChange={onScrollingStateChange}
              itemContent={index => (
                <ServiceLineItem
                  forwardMapping={forwardMapping}
                  comparator={comparator}
                  displayCoverage={displayCoverage}
                  onSelect={async (selected) => {
                    if (selected) {
                      trackEvent('Create Booking Service Autocomplete List - select')
                      await doClearItem(items[index])
                    } else {
                      trackEvent('Create Booking Service Autocomplete List - remove')
                      await doAddItem(items[index])
                    }
                  }}
                  selectedItems={selectedItems}
                  item={items[index]}
                  index={index}
                />
              )}

              ItemContainer={AnimatedContainer}
            />

          </div>
        </Context.Provider>
      </DialogContent>
    </>
  )
}

const ServiceAutocompleteListAsDialog = ({
  open,
  onClose,
  isMobile,
  title,
  compact = true,
  ...props
}) => {
  const classes = useStyles()

  return (
    <Dialog open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      fullScreen={isMobile}
      scroll="body"
    >
      <DialogActions className={classes.topBar}>
        <div />
        <div className={classes.header}>
          <Typography variant="h6">
            {title || `Find ${props?.service?.type?.label} By Name`}
          </Typography>
        </div>
        {
          isMobile ? (
            <>
              
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </>

          ):(<div/>)
        }
      </DialogActions>
      <ServiceAutocompleteList
        compact={compact}
        {...props}
      />
      <DialogContent className={clsx(classes.swapPadding,)}>
        {props.children}
      </DialogContent>

    </Dialog>
  )
}

export { ServiceAutocompleteListAsDialog, ServiceAutocompleteList };
